import * as React from 'react';
import camelCase from 'lodash/camelCase';

import { InputRadioCardField } from '../RadioCard/InputRadioCardField';
import { InputRadioBadgeField } from '../RadioBadge/InputRadioBadgeField';
import { InputRadioToggleField } from '../RadioToggle/InputRadioToggleField';
import { IInputRadioField } from '../common';
import { useFocusControl } from '../../../Utils';
import { useFieldSetGroupContext } from '../../FieldSet/FieldSetGroup';

import { RadioInput, RadioLabel } from './RadioInputStyles';

export interface IInputRadioDefaultFieldProps extends IInputRadioField {
  multiLine?: boolean;
}

// exporting here is a work around for this issue https://github.com/microsoft/tslint-microsoft-contrib/issues/667
export const InputRadioDefaultField =
  React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & IInputRadioDefaultFieldProps>((
  {
    children,
    groupName: groupNameComponent,
    hasError: hasErrorComponent,
    inputProps,
    multiLine,
    value,
  },
  ref
) => {
  const { clickFocus, stopClickFocus, resetClickFocus } = useFocusControl();
  const { errorIds, groupName, hasError, helperTextId } = useFieldSetGroupContext();

  const inputId: string = camelCase(`${groupName || groupNameComponent} ${value} id`);

  const handleOnBlur: (e: React.FocusEvent<HTMLInputElement>) => void = e => {
    resetClickFocus();
    if (inputProps && inputProps.onBlur) {
      inputProps.onBlur(e);
    }
  };

  return (
    <>
      <RadioInput
        {...inputProps}
        aria-describedby={errorIds ? `${errorIds} ${helperTextId && helperTextId}` : helperTextId}
        error={hasError || hasErrorComponent}
        id={inputId}
        multiLine={multiLine}
        name={groupName || groupNameComponent}
        ref={ref}
        type='radio'
        value={value}
        clickFocus={clickFocus}
        onBlur={handleOnBlur}
      />
      <RadioLabel
        htmlFor={inputId}
        error={hasError}
        onMouseDown={stopClickFocus}
      >
        <span>{children}</span>
      </RadioLabel>
    </>
  );
});

const InputRadioField = {
  button: InputRadioBadgeField,
  card: InputRadioCardField,
  radio: InputRadioDefaultField,
  toggle: InputRadioToggleField,
};

InputRadioField.button.displayName = 'InputRadioField.button';
InputRadioField.card.displayName = 'InputRadioField.card';
InputRadioField.radio.displayName = 'InputRadioField.radio';
InputRadioField.toggle.displayName = 'InputRadioField.toggle';

export default InputRadioField;
